import { useState, useEffect, useMemo } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Save, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { useHorariosDisponiveis } from "@/hooks/useHorariosDisponiveis";
import { useAutomaticBrazilLookup } from "@/hooks/useAutomaticBrazilLookup";
import { toast } from "@/hooks/use-toast";
import { lookupCep, lookupCnpj, maskCep, maskCnpj, maskPhone } from "@/lib/brasil";

const ESTADOS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

const DIAS_SEMANA_MAP: Record<number, string> = {
  0: "Domingo",
  1: "Segunda-feira",
  2: "Terça-feira",
  3: "Quarta-feira",
  4: "Quinta-feira",
  5: "Sexta-feira",
  6: "Sábado",
};

type ExpedienteDia = {
  id: string;
  dia_semana: number;
  ativo: boolean;
  hora: string;
  hora_fim: string;
  duracao_minutos: number;
};

const defaultExpediente = (diaSemana: number): ExpedienteDia[] => diaSemana >= 1 && diaSemana <= 6 ? [
  {
    id: "",
    dia_semana: diaSemana,
    ativo: true,
    hora: "09:00",
    hora_fim: "12:00",
    duracao_minutos: 60,
  },
  {
    id: "",
    dia_semana: diaSemana,
    ativo: true,
    hora: "14:00",
    hora_fim: "19:00",
    duracao_minutos: 60,
  },
] : [{
  id: "",
  dia_semana: diaSemana,
  ativo: false,
  hora: "",
  hora_fim: "",
  duracao_minutos: 60,
}];

const novoBlocoExpediente = (diaSemana: number): ExpedienteDia => ({
  id: "",
  dia_semana: diaSemana,
  ativo: true,
  hora: "09:00",
  hora_fim: "12:00",
  duracao_minutos: 60,
});

const gerarPreviaHorarios = (inicio: string, fim: string, duracao: number) => {
  if (!inicio || !fim || !duracao || duracao <= 0 || inicio >= fim) return [];

  const [inicioHora, inicioMinuto] = inicio.split(":").map(Number);
  const [fimHora, fimMinuto] = fim.split(":").map(Number);
  const inicioTotal = inicioHora * 60 + inicioMinuto;
  const fimTotal = fimHora * 60 + fimMinuto;
  const slots: string[] = [];

  for (let cursor = inicioTotal; cursor + duracao <= fimTotal; cursor += duracao) {
    const hora = Math.floor(cursor / 60).toString().padStart(2, "0");
    const minuto = (cursor % 60).toString().padStart(2, "0");
    const fimSlot = cursor + duracao;
    const horaFim = Math.floor(fimSlot / 60).toString().padStart(2, "0");
    const minutoFim = (fimSlot % 60).toString().padStart(2, "0");
    slots.push(`${hora}:${minuto} às ${horaFim}:${minutoFim}`);
  }

  return slots;
};

export function InformacoesClinica() {
  const { informacoes, isLoading, saveInformacoes, isSaving } = useInformacoesClinica();
  const { horarios, isLoading: isLoadingHorarios, createHorario, updateHorario, deleteHorario } = useHorariosDisponiveis();
  
  const [formData, setFormData] = useState({
    nome_clinica: "",
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    cro_clinica: "",
    telefone: "",
    celular: "",
    email: "",
    website: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    logo_base64: "",
    observacoes: "",
    situacao_cadastral: "",
    cnae_principal: "",
    natureza_juridica: "",
    porte: "",
    data_abertura: "",
  });

  const [autoCnpjEnabled, setAutoCnpjEnabled] = useState(false);
  const [autoCepEnabled, setAutoCepEnabled] = useState(false);

  const expedientesPorDia = useMemo(() => {
    return Object.keys(DIAS_SEMANA_MAP).reduce((acc, dia) => {
      const diaSemana = Number(dia);
      const existentes = horarios
        .filter((horario) => horario.dia_semana === diaSemana && horario.tipo === "expediente")
        .sort((a, b) => String(a.hora).localeCompare(String(b.hora)));

      acc[diaSemana] = existentes.length
        ? existentes.map((existente) => ({
            id: existente.id,
            dia_semana: diaSemana,
            ativo: existente.ativo,
            hora: (existente.hora || "").substring(0, 5),
            hora_fim: (existente.hora_fim || "").substring(0, 5),
            duracao_minutos: Number(existente.duracao_minutos || 60),
          }))
        : defaultExpediente(diaSemana);

      return acc;
    }, {} as Record<number, ExpedienteDia[]>);
  }, [horarios]);

  const [expedientesEditados, setExpedientesEditados] = useState<Record<number, ExpedienteDia[]>>({});

  useEffect(() => {
    setExpedientesEditados(expedientesPorDia);
  }, [expedientesPorDia]);

  useEffect(() => {
    if (informacoes) {
      setFormData({
        nome_clinica: informacoes.nome_clinica || "",
        razao_social: informacoes.razao_social || "",
        nome_fantasia: informacoes.nome_fantasia || "",
        cnpj: informacoes.cnpj || "",
        cro_clinica: informacoes.cro_clinica || "",
        telefone: informacoes.telefone || "",
        celular: informacoes.celular || "",
        email: informacoes.email || "",
        website: informacoes.website || "",
        endereco: informacoes.endereco || "",
        numero: informacoes.numero || "",
        complemento: informacoes.complemento || "",
        bairro: informacoes.bairro || "",
        cidade: informacoes.cidade || "",
        estado: informacoes.estado || "",
        cep: informacoes.cep || "",
        logo_base64: informacoes.logo_base64 || "",
        observacoes: informacoes.observacoes || "",
        situacao_cadastral: informacoes.situacao_cadastral || "",
        cnae_principal: informacoes.cnae_principal || "",
        natureza_juridica: informacoes.natureza_juridica || "",
        porte: informacoes.porte || "",
        data_abertura: informacoes.data_abertura || "",
      });
    }
  }, [informacoes]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const cnpjLookup = useAutomaticBrazilLookup({
    value: formData.cnpj,
    digits: 14,
    enabled: autoCnpjEnabled,
    lookup: lookupCnpj,
    onSuccess: (info) => {
      setFormData(prev => ({
        ...prev,
        cnpj: info.cnpj,
        nome_clinica: prev.nome_clinica || info.nome_fantasia || info.razao_social,
        razao_social: info.razao_social,
        nome_fantasia: info.nome_fantasia,
        telefone: info.telefone || prev.telefone,
        email: info.email || prev.email,
        cep: info.cep,
        endereco: info.endereco,
        numero: info.numero,
        complemento: info.complemento,
        bairro: info.bairro,
        cidade: info.cidade,
        estado: info.estado,
        situacao_cadastral: info.situacao_cadastral,
        cnae_principal: info.cnae_principal,
        natureza_juridica: info.natureza_juridica,
        porte: info.porte,
        data_abertura: info.data_abertura,
      }));
      toast({ title: "CNPJ encontrado", description: "Dados da clinica preenchidos automaticamente." });
    },
    onError: (error) => {
      toast({ title: "CNPJ nao encontrado", description: error.message, variant: "destructive" });
    },
  });

  const cepLookup = useAutomaticBrazilLookup({
    value: formData.cep,
    digits: 8,
    enabled: autoCepEnabled,
    lookup: lookupCep,
    onSuccess: (info) => {
      setFormData(prev => ({
        ...prev,
        cep: info.cep,
        endereco: info.logradouro || prev.endereco,
        complemento: info.complemento || prev.complemento,
        bairro: info.bairro || prev.bairro,
        cidade: info.cidade || prev.cidade,
        estado: info.estado || prev.estado,
      }));
      toast({ title: "CEP encontrado", description: "Endereco preenchido automaticamente." });
    },
    onError: (error) => {
      toast({ title: "CEP nao encontrado", description: error.message, variant: "destructive" });
    },
  });

  const handleExpedienteChange = (diaSemana: number, index: number, field: keyof ExpedienteDia, value: string | boolean | number) => {
    setExpedientesEditados(prev => ({
      ...prev,
      [diaSemana]: (prev[diaSemana] || defaultExpediente(diaSemana)).map((expediente, currentIndex) =>
        currentIndex === index ? { ...expediente, [field]: value } : expediente
      ),
    }));
  };

  const adicionarBloco = (diaSemana: number) => {
    setExpedientesEditados(prev => ({
      ...prev,
      [diaSemana]: [...(prev[diaSemana] || defaultExpediente(diaSemana)), novoBlocoExpediente(diaSemana)],
    }));
  };

  const removerBloco = (diaSemana: number, index: number) => {
    setExpedientesEditados(prev => {
      const atuais = prev[diaSemana] || defaultExpediente(diaSemana);
      const next = atuais.filter((_, currentIndex) => currentIndex !== index);
      return { ...prev, [diaSemana]: next.length ? next : defaultExpediente(diaSemana).map((item) => ({ ...item, ativo: false })) };
    });
  };

  const aplicarPadraoAtendimento = () => {
    setExpedientesEditados(
      Object.keys(DIAS_SEMANA_MAP).reduce((acc, dia) => {
        const diaSemana = Number(dia);
        const existentes = expedientesEditados[diaSemana] || expedientesPorDia[diaSemana] || [];
        const ids = existentes.map((item) => item.id).filter(Boolean);
        acc[diaSemana] = defaultExpediente(diaSemana).map((item, index) => ({ ...item, id: ids[index] || "" }));
        return acc;
      }, {} as Record<number, ExpedienteDia[]>)
    );
  };

  const handleSaveExpediente = (diaSemana: number) => {
    const expedientes = expedientesEditados[diaSemana] || defaultExpediente(diaSemana);
    const ativos = expedientes.filter((expediente) => expediente.ativo);

    if (ativos.some((expediente) => !expediente.hora || !expediente.hora_fim)) {
      toast({
        title: "Expediente incompleto",
        description: "Informe o horário de início e fim do dia.",
        variant: "destructive",
      });
      return;
    }

    if (ativos.some((expediente) => expediente.hora >= expediente.hora_fim)) {
      toast({
        title: "Horário inválido",
        description: "O horário de fim precisa ser maior que o horário de início.",
        variant: "destructive",
      });
      return;
    }

    const ordenados = [...ativos].sort((a, b) => a.hora.localeCompare(b.hora));
    const temSobreposicao = ordenados.some((expediente, index) => {
      const anterior = ordenados[index - 1];
      return !!anterior && anterior.hora_fim > expediente.hora;
    });

    if (temSobreposicao) {
      toast({
        title: "Expedientes sobrepostos",
        description: "Os blocos do mesmo dia não podem se cruzar.",
        variant: "destructive",
      });
      return;
    }

    const idsMantidos = new Set(expedientes.map((item) => item.id).filter(Boolean));
    horarios
      .filter((horario) => horario.dia_semana === diaSemana && horario.tipo === "expediente" && horario.id && !idsMantidos.has(horario.id))
      .forEach((horario) => deleteHorario(horario.id));

    expedientes.forEach((expediente) => {
      const payload = {
        dia_semana: diaSemana,
        hora: expediente.hora || "09:00",
        hora_fim: expediente.hora_fim || "12:00",
        duracao_minutos: Number(expediente.duracao_minutos || 60),
        tipo: "expediente" as const,
        ativo: expediente.ativo,
      };

      if (expediente.id) updateHorario({ id: expediente.id, ...payload });
      else if (expediente.ativo) createHorario(payload);
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange("logo_base64", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveInformacoes(formData);
  };

  if (isLoading || isLoadingHorarios) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            Informações da Clínica
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure os dados principais da sua clínica odontológica
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Básicos */}
          <Card>
            <CardHeader>
              <CardTitle>Dados Básicos</CardTitle>
              <CardDescription>Informações principais da clínica</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_clinica">Nome da Clínica*</Label>
                  <Input
                    id="nome_clinica"
                    value={formData.nome_clinica}
                    onChange={(e) => handleChange("nome_clinica", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    value={formData.cnpj}
                    onChange={(e) => {
                      setAutoCnpjEnabled(true);
                      handleChange("cnpj", maskCnpj(e.target.value));
                    }}
                    placeholder="00.000.000/0000-00"
                  />
                  {cnpjLookup.loading && <p className="text-xs text-muted-foreground">Consultando dados na Receita Federal...</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão social</Label>
                  <Input id="razao_social" value={formData.razao_social} onChange={(e) => handleChange("razao_social", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome_fantasia">Nome fantasia</Label>
                  <Input id="nome_fantasia" value={formData.nome_fantasia} onChange={(e) => handleChange("nome_fantasia", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cro_clinica">CRO da Clínica</Label>
                  <Input
                    id="cro_clinica"
                    value={formData.cro_clinica}
                    onChange={(e) => handleChange("cro_clinica", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone*</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => handleChange("telefone", maskPhone(e.target.value))}
                    placeholder="(00) 0000-0000"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="celular">Celular</Label>
                  <Input
                    id="celular"
                    value={formData.celular}
                    onChange={(e) => handleChange("celular", maskPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail*</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange("website", e.target.value)}
                    placeholder="https://www.suaclinica.com.br"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card>
            <CardHeader>
              <CardTitle>Endereço</CardTitle>
              <CardDescription>Localização da clínica</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="endereco">Endereço*</Label>
                  <Input
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) => handleChange("endereco", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    value={formData.numero}
                    onChange={(e) => handleChange("numero", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={formData.complemento}
                    onChange={(e) => handleChange("complemento", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => handleChange("bairro", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cep">CEP*</Label>
                  <Input
                    id="cep"
                    value={formData.cep}
                    onChange={(e) => {
                      setAutoCepEnabled(true);
                      handleChange("cep", maskCep(e.target.value));
                    }}
                    placeholder="00000-000"
                    required
                  />
                  {cepLookup.loading && <p className="text-xs text-muted-foreground">Consultando CEP...</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade*</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => handleChange("cidade", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estado">Estado*</Label>
                  <Select
                    value={formData.estado}
                    onValueChange={(value) => handleChange("estado", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS.map((estado) => (
                        <SelectItem key={estado} value={estado}>
                          {estado}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Horários de Agendamento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horários de Agendamento
              </CardTitle>
              <CardDescription>
                Configure o expediente por dia e a duração padrão. A agenda gera os horários automaticamente e oculta os ocupados.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">Padrão recomendado</p>
                  <p className="text-xs text-muted-foreground">09:00 às 12:00 e 14:00 às 19:00. O almoço fica automaticamente indisponível.</p>
                </div>
                <Button type="button" variant="outline" onClick={aplicarPadraoAtendimento}>
                  Aplicar padrão
                </Button>
              </div>

              <div className="space-y-3">
                {Object.entries(DIAS_SEMANA_MAP).map(([diaNum, diaLabel]) => {
                  const diaSemana = Number(diaNum);
                  const expedientes = expedientesEditados[diaSemana] || expedientesPorDia[diaSemana] || defaultExpediente(diaSemana);
                  const ativoDia = expedientes.some((expediente) => expediente.ativo);
                  const previa = expedientes
                    .filter((expediente) => expediente.ativo)
                    .flatMap((expediente) => gerarPreviaHorarios(expediente.hora, expediente.hora_fim, Number(expediente.duracao_minutos || 60)));

                  return (
                    <div key={diaNum} className="rounded-lg border bg-background p-4">
                      <div className="grid gap-4 lg:grid-cols-[180px_1fr_auto] lg:items-start">
                        <div className="flex items-center gap-3 pt-1">
                          <Switch
                            checked={ativoDia}
                            onCheckedChange={(checked) => {
                              setExpedientesEditados((prev) => ({
                                ...prev,
                                [diaSemana]: (prev[diaSemana] || expedientes).map((expediente) => ({ ...expediente, ativo: checked })),
                              }));
                            }}
                          />
                          <div>
                            <h4 className="font-semibold text-sm">{diaLabel}</h4>
                            <p className="text-xs text-muted-foreground">{ativoDia ? `${expedientes.filter((item) => item.ativo).length} bloco(s)` : "Fechado"}</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {expedientes.map((expediente, index) => {
                            const duracao = Number(expediente.duracao_minutos || 60);
                            const horas = Math.floor(duracao / 60);
                            const minutos = duracao % 60;

                            return (
                              <div key={`${diaNum}-${index}-${expediente.id || "novo"}`} className="grid gap-3 rounded-md border bg-muted/20 p-3 sm:grid-cols-[90px_repeat(4,minmax(0,1fr))_auto] sm:items-end">
                                <div className="flex items-center gap-2 pb-2">
                                  <Switch
                                    checked={!!expediente.ativo}
                                    onCheckedChange={(checked) => handleExpedienteChange(diaSemana, index, "ativo", checked)}
                                  />
                                  <span className="text-xs font-medium">Bloco {index + 1}</span>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`inicio-${diaNum}-${index}`}>Início</Label>
                                  <Input
                                    id={`inicio-${diaNum}-${index}`}
                                    type="time"
                                    value={expediente.hora}
                                    disabled={!expediente.ativo}
                                    onChange={(e) => handleExpedienteChange(diaSemana, index, "hora", e.target.value)}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`fim-${diaNum}-${index}`}>Fim</Label>
                                  <Input
                                    id={`fim-${diaNum}-${index}`}
                                    type="time"
                                    value={expediente.hora_fim}
                                    disabled={!expediente.ativo}
                                    onChange={(e) => handleExpedienteChange(diaSemana, index, "hora_fim", e.target.value)}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`duracao-horas-${diaNum}-${index}`}>Horas</Label>
                                  <Input
                                    id={`duracao-horas-${diaNum}-${index}`}
                                    type="number"
                                    min="0"
                                    value={horas}
                                    disabled={!expediente.ativo}
                                    onChange={(e) => {
                                      const novasHoras = Math.max(0, Number(e.target.value || 0));
                                      handleExpedienteChange(diaSemana, index, "duracao_minutos", Math.max(5, novasHoras * 60 + minutos));
                                    }}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`duracao-minutos-${diaNum}-${index}`}>Minutos</Label>
                                  <Input
                                    id={`duracao-minutos-${diaNum}-${index}`}
                                    type="number"
                                    min="0"
                                    max="59"
                                    step="5"
                                    value={minutos}
                                    disabled={!expediente.ativo}
                                    onChange={(e) => {
                                      const novosMinutos = Math.min(59, Math.max(0, Number(e.target.value || 0)));
                                      handleExpedienteChange(diaSemana, index, "duracao_minutos", Math.max(5, horas * 60 + novosMinutos));
                                    }}
                                  />
                                </div>
                                <Button type="button" variant="ghost" onClick={() => removerBloco(diaSemana, index)}>
                                  Remover
                                </Button>
                              </div>
                            );
                          })}

                          <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" onClick={() => adicionarBloco(diaSemana)}>
                              Adicionar bloco
                            </Button>
                            <Button type="button" onClick={() => handleSaveExpediente(diaSemana)}>
                              Salvar dia
                            </Button>
                          </div>
                        </div>
                      </div>

                      {ativoDia && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {previa.length > 0 ? (
                            <>
                              {previa.slice(0, 18).map((horario) => (
                                <span key={horario} className="rounded-md bg-muted px-2 py-1 text-xs font-mono text-muted-foreground">
                                  {horario}
                                </span>
                              ))}
                              {previa.length > 18 && (
                                <span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
                                  +{previa.length - 18} horários
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Ajuste início, fim e duração para visualizar os horários gerados.
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {(formData.situacao_cadastral || formData.cnae_principal || formData.natureza_juridica) && (
                <div className="grid grid-cols-1 gap-4 rounded-md border bg-muted/20 p-4 md:grid-cols-2">
                  <div><Label>Situação cadastral</Label><p className="text-sm">{formData.situacao_cadastral || "-"}</p></div>
                  <div><Label>Data de abertura</Label><p className="text-sm">{formData.data_abertura || "-"}</p></div>
                  <div><Label>Atividade principal</Label><p className="text-sm">{formData.cnae_principal || "-"}</p></div>
                  <div><Label>Natureza jurídica / porte</Label><p className="text-sm">{[formData.natureza_juridica, formData.porte].filter(Boolean).join(" - ") || "-"}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Logo e Observações */}
          <Card>
            <CardHeader>
              <CardTitle>Logo e Observações</CardTitle>
              <CardDescription>Adicione a logo da clínica e informações adicionais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo">Logo da Clínica</Label>
                <div className="flex items-center gap-4">
                  {formData.logo_base64 && (
                    <img
                      src={formData.logo_base64}
                      alt="Logo"
                      className="h-20 w-20 object-contain border rounded"
                    />
                  )}
                  <div className="flex-1">
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => handleChange("observacoes", e.target.value)}
                  rows={4}
                  placeholder="Informações adicionais sobre a clínica..."
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? "Salvando..." : "Salvar Informações"}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
